import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from "react";
import { useInView } from "react-intersection-observer";

export interface ResponsiveImageProps {
  src: string;
  alt?: string;
  title?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  loading?: "lazy" | "eager";
  decoding?: "async" | "sync" | "auto";
  fetchPriority?: "high" | "low" | "auto";
  sizes?: string;
  srcSet?: string;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  priority?: boolean;
  placeholder?: "empty" | "blur";
  blurDataURL?: string;
  quality?: number;
  style?: React.CSSProperties;
  minifiedSrc?: string;
  breakpoints?: number[];
  format?: "webp" | "jpg" | "png";
  artDirection?: Record<number, string>;
  itemProp?: string;
  project?: string;
}

const DEFAULT_BREAKPOINTS = [400, 1200];
const DEFAULT_SIZES = "(max-width: 768px) 100vw, 1200px";

/**
 * Extracts base filename from src path (removes directory and extension)
 * Replaces spaces with underscores for consistency with optimized files
 */
const extractBaseName = (src: string): string => {
  const filename = src.split("/").pop() || "";
  const baseName = filename.replace(/\.[^/.]+$/, "");
  return baseName.replace(/\s+/g, "_");
};

/**
 * Generates SEO-friendly alt text from filename
 * Converts kebab-case, snake_case, and camelCase to readable text
 */
const generateSeoAltText = (src: string): string => {
  const baseName = extractBaseName(src);
  const cleanName = baseName
    .replace(/-minified$/, "")
    .replace(/-\d+w$/, "")
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  return cleanName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Generates image title from filename (more concise than alt)
 */
const generateImageTitle = (src: string): string => {
  const baseName = extractBaseName(src);
  const cleanName = baseName
    .replace(/-minified$/, "")
    .replace(/-\d+w$/, "")
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .slice(0, 5)
    .join(" ");
  return cleanName.charAt(0).toUpperCase() + cleanName.slice(1).toLowerCase();
};

/**
 * Generates optimized image path for a specific width
 * Always uses /optimized/ directory with project support
 */
const getOptimizedImagePath = (
  src: string,
  width: number | "full" | "minified",
  format: "webp" | "jpg" | "png" = "webp",
  project?: string
): string => {
  const baseName = extractBaseName(src);
  const optimizedBaseName = baseName
    .replace(/-minified$/, "")
    .replace(/-\d+w$/, "");

  const projectPath = project && project !== "root" ? `${project}/` : "";

  if (width === "minified") {
    return `/optimized/${projectPath}${optimizedBaseName}-minified.webp`;
  } else if (width === "full") {
    return `/optimized/${projectPath}${optimizedBaseName}.${format}`;
  } else {
    return `/optimized/${projectPath}${optimizedBaseName}-${width}w.${format}`;
  }
};

/**
 * Gets the optimal format based on transparency and browser support
 */
const getOptimalFormat = (): "webp" => {
  return "webp";
};

/**
 * Generates srcSet string from breakpoints with project support
 */
const generateSrcSet = (
  src: string,
  breakpoints: number[],
  project?: string
): string => {
  const format = getOptimalFormat();
  return breakpoints
    .map((bp) => {
      const optimizedPath = getOptimizedImagePath(src, bp, format, project);
      return `${optimizedPath} ${bp}w`;
    })
    .join(", ");
};

/**
 * Main Image Component - Pure ghost wrapper for <img>
 * Always uses optimized paths for both src and srcSet
 * Enhanced with SEO attributes
 */
export const Image: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  title,
  width,
  height,
  className = "",
  loading = "lazy",
  decoding = "async",
  fetchPriority = "auto",
  sizes = DEFAULT_SIZES,
  srcSet: propSrcSet,
  onLoad,
  onError,
  priority = false,
  placeholder = "empty",
  blurDataURL,
  quality = 80,
  style,
  minifiedSrc: propMinifiedSrc,
  breakpoints = DEFAULT_BREAKPOINTS,
  format = "webp",
  artDirection,
  itemProp,
  project,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>("");
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const { ref: viewRef, inView } = useInView({
    threshold: 0.01,
    rootMargin: "200px",
    triggerOnce: true
  });

  // Generate SEO-friendly alt text from filename if not provided
  const seoAlt = useMemo(() => {
    if (alt) return alt;
    return generateSeoAltText(src);
  }, [alt, src]);

  // Generate image title from filename if not provided
  const imageTitle = useMemo(() => {
    if (title) return title;
    return generateImageTitle(src);
  }, [title, src]);

  // Always use optimized paths with project support
  const minifiedSrc = useMemo(
    () =>
      propMinifiedSrc ||
      getOptimizedImagePath(src, "minified", "webp", project),
    [propMinifiedSrc, src, project]
  );

  const fullSizeSrc = useMemo(
    () => getOptimizedImagePath(src, "full", getOptimalFormat(), project),
    [src, project]
  );

  const generatedSrcSet = useMemo(
    () => propSrcSet || generateSrcSet(src, breakpoints, project),
    [propSrcSet, src, breakpoints, project]
  );

  // Get optimal image source based on viewport and priority
  const getOptimalImage = useCallback((): string => {
    // Use art direction if provided
    if (artDirection) {
      const sortedBreakpoints = Object.keys(artDirection)
        .map(Number)
        .sort((a, b) => a - b);
      let selectedSrc = src;
      // Still use optimized version of the art-directed source
      return getOptimizedImagePath(
        selectedSrc,
        "full",
        getOptimalFormat(),
        project
      );
    }

    // For priority images or when in view, use the full optimized version
    if (priority || inView) {
      return fullSizeSrc;
    }

    // For lazy loading, start with minified
    return minifiedSrc;
  }, [priority, inView, src, minifiedSrc, fullSizeSrc, artDirection, project]);

  // Handle image loading
  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoaded(true);
      onLoad?.(e);
    },
    [onLoad]
  );

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      console.error("Image failed to load:", e.currentTarget.src);
      setHasError(true);
      const attemptedSrc = e.currentTarget.src;

      // Fallback strategies for optimized paths
      if (attemptedSrc.includes("/optimized/")) {
        // 1. Try PNG if WebP failed
        if (attemptedSrc.includes(".webp")) {
          const pngFallback = attemptedSrc.replace(".webp", ".png");
          setCurrentSrc(pngFallback);
          return;
        }
        // 2. Try the original file from optimized directory
        if (
          attemptedSrc.includes("-minified.") ||
          attemptedSrc.includes("-400w.") ||
          attemptedSrc.includes("-1200w.")
        ) {
          // Fall back to the full optimized version
          setCurrentSrc(fullSizeSrc);
          return;
        }
      }

      // 3. Last resort: try the original source path
      if (attemptedSrc !== src) {
        setCurrentSrc(src);
        return;
      }

      onError?.(e);
    },
    [src, fullSizeSrc, onError]
  );

  // Update source when in view (progressive enhancement)
  useEffect(() => {
    if (inView && !isLoaded && !hasError) {
      const optimalSrc = getOptimalImage();
      if (optimalSrc !== currentSrc && optimalSrc !== minifiedSrc) {
        setCurrentSrc(optimalSrc);
      }
    }
  }, [inView, isLoaded, hasError, currentSrc, minifiedSrc, getOptimalImage]);

  // Initial source setup - always start with optimized path
  useEffect(() => {
    const initialSrc = priority ? fullSizeSrc : minifiedSrc;
    setCurrentSrc(initialSrc);
    if (priority) {
      setIsLoaded(true);
    }
  }, [priority, minifiedSrc, fullSizeSrc]);

  // Preload priority images
  useEffect(() => {
    if (priority && currentSrc && currentSrc !== minifiedSrc) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = currentSrc;
      document.head.appendChild(link);
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [priority, currentSrc, minifiedSrc]);

  // Clean up ref
  const handleRef = useCallback(
    (node: HTMLImageElement | null) => {
      viewRef(node);
      imgRef.current = node;
    },
    [viewRef]
  );

  // SSR handling - use optimized paths even during SSR
  if (typeof window === "undefined") {
    return (
      <img
        src={fullSizeSrc}
        alt={seoAlt}
        title={imageTitle}
        width={width}
        height={height}
        className={className}
        loading={priority ? "eager" : loading}
        decoding={decoding}
        sizes={sizes}
        srcSet={generatedSrcSet}
        style={style}
        itemProp={itemProp}
        {...props}
      />
    );
  }

  // Don't render at all if no source
  if (!currentSrc) return null;

  // Always use the optimized srcSet
  const finalSrcSet = generatedSrcSet;

  // Single img element - pure ghost wrapper with SEO enhancements
  return (
    <img
      ref={handleRef}
      src={currentSrc}
      alt={seoAlt}
      title={imageTitle}
      width={width}
      height={height}
      className={className}
      loading={priority ? "eager" : loading}
      decoding={decoding}
      sizes={sizes}
      srcSet={finalSrcSet}
      style={{
        ...style,
        opacity: isLoaded ? 1 : 0.9,
        transition: "opacity 0.2s ease",
        ...(placeholder === "blur" && blurDataURL
          ? {
              backgroundImage: `url(${blurDataURL})`,
              backgroundSize: "cover"
            }
          : {})
      }}
      onLoad={handleLoad}
      onError={handleError}
      data-image-loaded={isLoaded ? "true" : "false"}
      data-image-in-view={inView ? "true" : "false"}
      data-image-optimized="true"
      itemProp={itemProp}
      {...props}
    />
  );
};

export default Image;
